import React from "react";
import { DateTime, Info } from "luxon";
import { FormControl, Select, SelectChangeEvent } from "@mui/material";
import { alpha, Theme } from "@mui/material/styles";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useTranslation } from "react-i18next";
import { ArcButton, ArcIconButton, ArcMenuItem, ArcStack, ArcText } from "@src/core/ui";
import { ReactComponent as CalendarIcon } from "@src/assets/icons/calendar.svg";
import { formatFilterDay } from "./date-range";

type RangeField = "from" | "to";

interface DateRangeCalendarProps {
  start: string | null;
  end: string | null;
  onChange: (start: string | null, end: string | null) => void;
  onCancel: () => void;
  onApply: () => void;
}

const currentYear = DateTime.now().year;
const YEARS = Array.from({ length: 7 }, (_, i) => currentYear - 5 + i);

const borderColor = (theme: Theme) => alpha(theme.palette.common.white, 0.12);
const CELL_SELECTED_BG = "#9EA0F0";
const CELL_IN_RANGE_BG = "#9EA0F03D";
const CELL_HEIGHT = 36;
const FIELD_GAP_PX = 16;

const compactSelectSx = {
  height: 24,
  fontSize: 11,
  color: "text.primary",
  "& .MuiSelect-select": { py: 0, pl: 0.75, pr: "16px !important" },
  "& .MuiSelect-icon": { fontSize: 14, right: 2, color: "text.secondary" },
  "& .MuiOutlinedInput-notchedOutline": { borderColor },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor },
};

const dayGridSx = {
  display: "grid",
  gridTemplateColumns: "repeat(7, 1fr)",
  width: "100%",
  gap: 0,
} as const;

const dualFieldRowSx = {
  display: "grid",
  gridTemplateColumns: `1fr ${FIELD_GAP_PX}px 1fr`,
  width: "100%",
  alignItems: "center",
} as const;

const arrowBtnSx = {
  p: 0,
  minWidth: 0,
  width: 18,
  height: 18,
  pointerEvents: "auto",
} as const;

const RangeFieldInput: React.FC<{
  label: string;
  value: string | null;
  active: boolean;
  onSelect: () => void;
}> = ({ label, value, active, onSelect }) => (
  <ArcStack
    direction="row"
    alignItems="center"
    justifyContent="space-between"
    spacing={0.75}
    width="100%"
    role="button"
    aria-label={label}
    aria-pressed={active}
    onClick={onSelect}
    sx={{
      height: 32,
      px: 1,
      boxSizing: "border-box",
      cursor: "pointer",
      borderRadius: 1,
      border: 1,
      borderColor: active ? "brand.main" : borderColor,
      color: "text.secondary",
      "& path": { stroke: "currentColor" },
    }}>
    <ArcText fontSize={11} color={value ? "text.primary" : "text.secondary"} noWrap>
      {value ?? label}
    </ArcText>
    <CalendarIcon width={14} height={14} aria-hidden />
  </ArcStack>
);

const MonthYearControls: React.FC<{
  view: DateTime;
  months: string[];
  onViewChange: (next: DateTime) => void;
  onPrevMonth: () => void;
  onNextMonth: () => void;
  prevLabel: string;
  nextLabel: string;
}> = ({ view, months, onViewChange, onPrevMonth, onNextMonth, prevLabel, nextLabel }) => {
  const onMonthChange = (event: SelectChangeEvent<unknown>) =>
    onViewChange(view.set({ month: Number(event.target.value) }));
  const onYearChange = (event: SelectChangeEvent<unknown>) =>
    onViewChange(view.set({ year: Number(event.target.value) }));

  return (
    <ArcStack position="relative" width="100%">
      <ArcStack sx={dualFieldRowSx}>
        <ArcStack alignItems="flex-end" width="100%">
          <FormControl size="small" sx={{ width: 60, flexShrink: 0 }}>
            <Select
              value={view.month}
              onChange={onMonthChange}
              IconComponent={KeyboardArrowDownIcon}
              sx={compactSelectSx}>
              {months.map((name, index) => (
                <ArcMenuItem key={index} value={index + 1}>
                  {name}
                </ArcMenuItem>
              ))}
            </Select>
          </FormControl>
        </ArcStack>

        <ArcStack />

        <ArcStack alignItems="flex-start" width="100%">
          <FormControl size="small" sx={{ width: 66, flexShrink: 0 }}>
            <Select
              value={view.year}
              onChange={onYearChange}
              IconComponent={KeyboardArrowDownIcon}
              sx={compactSelectSx}>
              {YEARS.map((year) => (
                <ArcMenuItem key={year} value={year}>
                  {year}
                </ArcMenuItem>
              ))}
            </Select>
          </FormControl>
        </ArcStack>
      </ArcStack>

      <ArcStack
        sx={{
          ...dayGridSx,
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          zIndex: 1,
        }}>
        <ArcStack alignItems="flex-start" justifyContent="center">
          <ArcIconButton size="small" aria-label={prevLabel} onClick={onPrevMonth} sx={arrowBtnSx}>
            <ChevronLeftIcon sx={{ color: "common.white", fontSize: 18, display: "block" }} />
          </ArcIconButton>
        </ArcStack>
        <ArcStack sx={{ gridColumn: "2 / 7" }} />
        <ArcStack alignItems="flex-end" justifyContent="center">
          <ArcIconButton size="small" aria-label={nextLabel} onClick={onNextMonth} sx={arrowBtnSx}>
            <ChevronRightIcon sx={{ color: "common.white", fontSize: 18, display: "block" }} />
          </ArcIconButton>
        </ArcStack>
      </ArcStack>
    </ArcStack>
  );
};

const MonthGrid: React.FC<{
  view: DateTime;
  weekdays: string[];
  startDt: DateTime | null;
  endDt: DateTime | null;
  onDayClick: (day: DateTime) => void;
}> = ({ view, weekdays, startDt, endDt, onDayClick }) => {
  const cells = React.useMemo<(DateTime | null)[]>(() => {
    const firstDay = view.startOf("month");
    const daysInMonth = view.daysInMonth ?? 30;
    const leadingBlanks = firstDay.weekday - 1;
    return [
      ...Array.from({ length: leadingBlanks }, () => null),
      ...Array.from({ length: daysInMonth }, (_, i) => firstDay.set({ day: i + 1 })),
    ];
  }, [view]);

  const isEndpoint = (day: DateTime): boolean =>
    Boolean((startDt && day.hasSame(startDt, "day")) || (endDt && day.hasSame(endDt, "day")));
  const isInRange = (day: DateTime): boolean =>
    Boolean(startDt && endDt && day > startDt && day < endDt);

  return (
    <ArcStack spacing={0.75} width="100%">
      <ArcStack sx={dayGridSx}>
        {weekdays.map((weekday, index) => (
          <ArcText
            key={index}
            fontSize={10}
            textAlign="center"
            sx={{ color: (theme) => alpha(theme.palette.common.white, 0.32) }}>
            {weekday}
          </ArcText>
        ))}
      </ArcStack>

      <ArcStack sx={dayGridSx}>
        {cells.map((day, index) => {
          if (!day) return <ArcStack key={`blank-${index}`} />;

          const endpoint = isEndpoint(day);
          const inRange = isInRange(day);

          return (
            <ArcStack
              key={day.toISODate()}
              alignItems="center"
              justifyContent="center"
              onClick={() => onDayClick(day)}
              sx={{
                height: CELL_HEIGHT,
                cursor: "pointer",
                bgcolor: endpoint ? CELL_SELECTED_BG : inRange ? CELL_IN_RANGE_BG : "transparent",
                "&:hover": { bgcolor: endpoint ? CELL_SELECTED_BG : "action.hover" },
              }}>
              <ArcText fontSize={11} color={endpoint ? "text.night" : "text.primary"}>
                {day.day}
              </ArcText>
            </ArcStack>
          );
        })}
      </ArcStack>
    </ArcStack>
  );
};

const DateRangeCalendar: React.FC<DateRangeCalendarProps> = ({
  start,
  end,
  onChange,
  onCancel,
  onApply,
}) => {
  const { t, i18n } = useTranslation();
  const startDt = start ? DateTime.fromISO(start) : null;
  const endDt = end ? DateTime.fromISO(end) : null;

  const [activeField, setActiveField] = React.useState<RangeField>("from");
  const [view, setView] = React.useState<DateTime>(
    () => (startDt ?? DateTime.now()).startOf("month"),
  );

  const months = React.useMemo(
    () => Info.months("short", { locale: i18n.language }),
    [i18n.language],
  );
  const weekdays = React.useMemo(
    () => Info.weekdays("short", { locale: i18n.language }),
    [i18n.language],
  );

  const formatValue = (value: string | null): string | null =>
    value ? formatFilterDay(value) : null;

  const focusField = (field: RangeField) => {
    setActiveField(field);
    const focused = field === "from" ? startDt : endDt;
    if (focused) setView(focused.startOf("month"));
  };

  const handleDayClick = (day: DateTime) => {
    const iso = day.toISODate();

    if (activeField === "from") {
      onChange(iso, endDt && day > endDt ? null : end);
      setActiveField("to");
      return;
    }
    if (startDt && day < startDt) {
      onChange(iso, start);
      return;
    }
    onChange(start ?? iso, iso);
  };

  return (
    <ArcStack
      spacing={1.5}
      sx={{
        bgcolor: "background.paper",
        borderRadius: 1,
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
        p: 3,
      }}>
      <ArcStack sx={dualFieldRowSx}>
        <RangeFieldInput
          label={t("history.filter.calendar.from")}
          value={formatValue(start)}
          active={activeField === "from"}
          onSelect={() => focusField("from")}
        />
        <ArcStack />
        <RangeFieldInput
          label={t("history.filter.calendar.to")}
          value={formatValue(end)}
          active={activeField === "to"}
          onSelect={() => focusField("to")}
        />
      </ArcStack>

      <ArcStack pt={1}>
        <MonthYearControls
          view={view}
          months={months}
          onViewChange={setView}
          onPrevMonth={() => setView((m) => m.minus({ months: 1 }))}
          onNextMonth={() => setView((m) => m.plus({ months: 1 }))}
          prevLabel={t("history.filter.calendar.previousMonth")}
          nextLabel={t("history.filter.calendar.nextMonth")}
        />
      </ArcStack>

      <MonthGrid
        view={view}
        weekdays={weekdays}
        startDt={startDt}
        endDt={endDt}
        onDayClick={handleDayClick}
      />

      <ArcStack direction="row" spacing={2} pt={1.5}>
        <ArcButton.ghost fullWidth type="button" onClick={onCancel}>
          {t("history.filter.actions.cancel")}
        </ArcButton.ghost>
        <ArcButton.brand fullWidth type="button" disabled={!start || !end} onClick={onApply}>
          {t("history.filter.actions.apply")}
        </ArcButton.brand>
      </ArcStack>
    </ArcStack>
  );
};

export default DateRangeCalendar;
